"use client";

import { SettingsPageIntro } from "@saas/settings/components/SettingsLayout";
import { Pager } from "@saas/shared/components/Pager";
import { UserAvatar } from "@shared/components/UserAvatar";
import { orpc } from "@shared/lib/orpc";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import { Skeleton } from "@ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ui/components/table";
import { cn } from "@ui/lib";
import { format } from "date-fns";
import { ScrollTextIcon } from "lucide-react";
import type { AuditLogEntry } from "../lib/types";

const routeApi = getRouteApi(
	"/_saas/app/_org/$organizationSlug/settings/audit",
);

const PAGE_SIZE = 20;

const CATEGORIES: Record<string, string> = {
	all: "Everything",
	agent_config: "Agent",
	catalog: "Catalog",
	auth: "Sign-ins",
	user: "Users",
	organization: "Retailer",
	member: "Team",
	role: "Roles",
	api_key: "API keys",
	webhook: "Webhooks",
	embed: "Install",
	profile: "Profiles",
	session: "Sessions",
	data: "Shopper data",
	domain: "Domains",
};

/** Actions whose words read badly as they are. */
const ACTION_TEXT: Record<string, string> = {
	"auth.login": "Signed in",
};

/** "member.role_updated" → "Member role updated" */
function describeAction(action: string) {
	const text =
		ACTION_TEXT[action] ??
		action
			.replace(/[._]/g, " ")
			.replace(/\bapi\b/g, "API")
			.trim();
	return text.charAt(0).toUpperCase() + text.slice(1);
}

export function AuditLog({ organizationId }: { organizationId: string }) {
	const { page, action } = routeApi.useSearch();
	const navigate = routeApi.useNavigate();

	const { data, isPending, isPlaceholderData } = useQuery({
		...orpc.audit.organization.list.queryOptions({
			input: {
				organizationId,
				action: action || undefined,
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			},
		}),
		placeholderData: keepPreviousData,
	});

	const logs = (data?.logs as AuditLogEntry[] | undefined) ?? [];
	const filterBy = (next: string) =>
		navigate({ search: { page: 1, action: next }, replace: true });

	return (
		<>
			<SettingsPageIntro
				title="Audit log"
				info="Who did what in this retailer's admin: sign-ins, team changes, API keys and settings."
				actions={<CategoryFilter action={action} onChange={filterBy} />}
			/>

			{isPending ? (
				<AuditLogSkeleton />
			) : logs.length === 0 ? (
				<NoActivity action={action} onShowAll={() => filterBy("")} />
			) : (
				<div
					className={cn(
						"flex flex-col gap-4 transition-opacity",
						isPlaceholderData && "opacity-60",
					)}
				>
					<AuditLogTable logs={logs} />
					<AuditLogCards logs={logs} />
					<Pager
						page={page}
						pageSize={PAGE_SIZE}
						total={data?.total ?? 0}
						onPageChange={(next) =>
							navigate({
								search: (prev) => ({ ...prev, page: next }),
							})
						}
					/>
				</div>
			)}
		</>
	);
}

function CategoryFilter({
	action,
	onChange,
}: {
	action: string;
	onChange: (action: string) => void;
}) {
	return (
		<Select
			items={CATEGORIES}
			value={action || "all"}
			onValueChange={(value) =>
				onChange(value === "all" ? "" : String(value))
			}
		>
			<SelectTrigger aria-label="Show" className="w-44">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{Object.entries(CATEGORIES).map(([value, label]) => (
					<SelectItem key={value} value={value}>
						{label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

const SKELETON_ROWS = ["a", "b", "c", "d", "e", "f"];

function AuditLogSkeleton() {
	return (
		<div className="flex flex-col gap-3 rounded-3xl p-5 ring-1 ring-foreground/6">
			{SKELETON_ROWS.map((row) => (
				<Skeleton key={row} className="h-10 w-full" />
			))}
		</div>
	);
}

function NoActivity({
	action,
	onShowAll,
}: {
	action: string;
	onShowAll: () => void;
}) {
	return (
		<Empty className="rounded-3xl border">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<ScrollTextIcon />
				</EmptyMedia>
				<EmptyTitle>
					{action
						? `No ${CATEGORIES[action]?.toLowerCase() ?? "matching"} activity`
						: "No activity yet"}
				</EmptyTitle>
				<EmptyDescription>
					{action
						? "Try another filter."
						: "Changes in this admin show up here."}
				</EmptyDescription>
			</EmptyHeader>
			{action ? (
				<EmptyContent>
					<Button variant="outline" onClick={onShowAll}>
						Show everything
					</Button>
				</EmptyContent>
			) : null}
		</Empty>
	);
}

function loggedAt(log: AuditLogEntry) {
	return format(new Date(log.createdAt), "d MMM, HH:mm");
}

function AuditLogTable({ logs }: { logs: AuditLogEntry[] }) {
	return (
		<div className="overflow-hidden rounded-3xl bg-card ring-1 ring-foreground/6 max-lg:hidden dark:ring-foreground/10">
			<Table>
				<TableHeader>
					<TableRow className="hover:bg-transparent">
						<TableHead className="pl-5">When</TableHead>
						<TableHead>Who</TableHead>
						<TableHead>What</TableHead>
						<TableHead className="pr-5">From</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{logs.map((log) => (
						<TableRow key={log.id}>
							<TableCell className="whitespace-nowrap py-3 pl-5 text-muted-foreground tabular">
								{loggedAt(log)}
							</TableCell>
							<TableCell className="py-3">
								<Actor log={log} />
							</TableCell>
							<TableCell className="py-3">
								<What log={log} />
							</TableCell>
							<TableCell className="py-3 pr-5 font-mono text-muted-foreground text-xs">
								{log.ipAddress ?? "Not recorded"}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

function AuditLogCards({ logs }: { logs: AuditLogEntry[] }) {
	return (
		<ul className="flex flex-col divide-y divide-border/70 rounded-3xl bg-card px-4 ring-1 ring-foreground/6 lg:hidden dark:ring-foreground/10">
			{logs.map((log) => (
				<li key={log.id} className="flex flex-col gap-2 py-3">
					<div className="flex items-center justify-between gap-3">
						<Actor log={log} />
						<span className="shrink-0 text-muted-foreground text-xs tabular">
							{loggedAt(log)}
						</span>
					</div>
					<What log={log} />
				</li>
			))}
		</ul>
	);
}

function Actor({ log }: { log: AuditLogEntry }) {
	if (!log.user) {
		return (
			<span className="text-muted-foreground text-sm">
				Hephon (system)
			</span>
		);
	}
	return (
		<div className="flex min-w-0 items-center gap-2">
			<UserAvatar
				name={log.user.name || log.user.email}
				avatarUrl={log.user.image}
				className="size-7"
			/>
			<div className="min-w-0">
				<p className="truncate font-medium text-sm">{log.user.name}</p>
				<p className="truncate text-muted-foreground text-xs">
					{log.user.email}
				</p>
			</div>
		</div>
	);
}

function What({ log }: { log: AuditLogEntry }) {
	const category = log.action.split(".")[0] ?? "";
	return (
		<div className="flex min-w-0 flex-wrap items-center gap-2">
			<span className="text-sm">{describeAction(log.action)}</span>
			{CATEGORIES[category] ? (
				<Badge variant="secondary">{CATEGORIES[category]}</Badge>
			) : null}
			{log.resourceId ? (
				<span className="font-mono text-muted-foreground text-xs">
					{log.resourceType} {log.resourceId.slice(0, 8)}
				</span>
			) : null}
		</div>
	);
}
